import { getCategoryNameFromSlug } from './getCategoryNameFromSlug';
import { getCategorySlugsFromUrl } from './getCategorySlugsFromUrl';
import { titleCase } from '@utils/plp/utils/transforms';
import {
  isBrandPlp,
  isCollectionPlp,
  isTagsPlp,
  isSearchPage,
  parseAlgoliaUrl,
  localizedProductsSlug,
  isCanadianPlp,
} from './helpers';

export function getCategoryPageId(url: string, locale: string) {
  /**
   * This function tries to match up a url with a categoryPageId
   * PLPs come in three flavours: taxonomy, brand and custom collections
   *
   * brands: /products/brands/brand-name/(*)?
   * collections: /products/collections/* (no categoryPageId; these filter on
   *   Algolia `_collections` instead)
   * taxomony: /products/*
   *
   * As well as categories, we have known filters, which are used to scope a url
   * and therefore filter results. These are: genders and offers/promotions.
   * gender: mens, womens, kids, unisex
   * offers: deals, sale, clearance, new
   *
   * These filters don't (shouldn't) exist as actual categories in BC, but they
   * do exist as categoryPageIds in Algolia. This ensures that the Merch team
   * can boost/bury/edit those pages.
   */

  try {
    let { pathname: pagePath } = new URL(url);
    const productsSlug = localizedProductsSlug(locale);

    // some _next/data .json urls were coming through
    // maybe just develeopment? Either way it was breaking back button
    pagePath = pagePath.replace('.json', '');

    if (isSearchPage(pagePath) || isCollectionPlp(pagePath)) {
      return '';
    }

    const { categorySlug, categoryFilters, filterPath } =
      getCategorySlugsFromUrl(pagePath, locale);
    const categoryFilterPath = titleCase(filterPath);

    // brand related page
    if (isBrandPlp(pagePath)) {
      const { brandIdentifier, brandCategoryFilter } = parseAlgoliaUrl(
        pagePath,
        locale
      );

      const brandName = getCategoryNameFromSlug(brandIdentifier, locale);

      if (brandCategoryFilter) {
        const brandSlugPath = brandCategoryFilter
          .split('/')
          .map((slug) => {
            return getCategoryNameFromSlug(slug, locale);
          })
          .map(titleCase)
          .join(' > ');

        return `${productsSlug} > ${brandSlugPath}`;
      }

      if (categoryFilters?.length) {
        return `${brandName} > ${categoryFilterPath}`;
      }

      return brandName;
    }

    // tag PLPs pull from /tags/*
    if (isTagsPlp(pagePath) || isCanadianPlp(pagePath, locale)) {
      const collectionPath = categorySlug
        .map((slug) => {
          return getCategoryNameFromSlug(slug, locale);
        })
        .join(' > ');

      if (categoryFilters?.length) {
        return `${collectionPath} > ${categoryFilterPath}`;
      }

      return collectionPath;
    }

    // taxonomy url
    if (categorySlug) {
      let categoryPath = productsSlug;

      const taxonomyPath = categorySlug
        .map((slug) => {
          return getCategoryNameFromSlug(slug, locale);
        })
        .join(' > ');

      if (taxonomyPath) {
        categoryPath = `${categoryPath} > ${taxonomyPath}`;
      }

      if (categoryFilters?.length) {
        return `${categoryPath} > ${categoryFilterPath}`;
      }

      return categoryPath;
    }

    return `${productsSlug}`;
  } catch (e) {
    console.error(e);
    return '';
  }
}
